import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    AttributionBenefModelDto,
    ChampListeValeurDto,
    ModeleChargementAttBdgBenfDto,
    ModeleExecAttributionBudgetDtoInput,
    MutationGetListeChampGeneriqueArgs,
    MutationGetListeSiteBaseModelArgs,
    MutationGetModeleChargementExecAttributionArgs,
    MutationUpdateQteAttribueeArgs,
    SiteBaseModel,
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { catchError, distinctUntilChanged, startWith, tap } from 'rxjs/operators';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { KalSelectionModel, KalSnackbarService } from '@kalidea/kaligraphi';
import { FormValidator } from 'src/app/validators/form-validator';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { getISODate, getKalDateFromObject } from 'src/app/utils/date.utils';
import { filterListFromSelection } from 'src/app/utils/list.utils';
import { forkJoin, merge, Observable, Subscription, throwError } from 'rxjs';
import {
    SharedAllocationExecution,
    SharedAllocationExecutionCode,
    SharedAllocationExecutionExecuteAllocationAction
} from 'src/app/_shared/stores/shared-allocation-execution/shared-allocation-execution.actions';
import { SharedAllocationExecutionState } from 'src/app/_shared/stores/shared-allocation-execution/shared-allocation-execution.state';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { ColumnsCSV, exportAmount, exportListAsCSV } from 'src/app/utils/kal-table-csv-export.utils';
import { GetListeExerciceInfosAction } from 'src/app/_shared/stores/exercices/exercices.action';
import { ExerciceState } from 'src/app/_shared/stores/exercices/exercices.state';
import { ActivitySalesState } from 'src/app/board/activity/stores/activity-sales/activity-sales.state';
import { AllocationsContext } from 'src/app/_shared/services/allocations/detail/allocation.service';
import { ProgressTrackerService } from 'src/app/_shared/components/progress/services/progress-tracker.service';
import { ATTRIBUTION_PREPARATION } from 'src/app/_shared/components/progress/config/progress-operations.config';

type SubventionAllocationExecution = AttributionBenefModelDto & { id: number, QuantiteControl: FormControl };

const LIBELLE_MAX_LENGTH = 50;

@Component({
    selector: 'app-shared-allocation-execution',
    templateUrl: './shared-allocation-execution.component.html',
    styleUrls: ['./shared-allocation-execution.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAllocationExecutionComponent<T extends SubventionAllocationExecution> extends BaseComponent implements OnInit {

    executionModel: ModeleChargementAttBdgBenfDto = null;

    form: FormGroup;

    filtersForm: FormGroup;

    beneficiariesList: T[] = [];

    displayedBeneficiariesList: T[] = [];

    selection = new KalSelectionModel<T>({multiple: true, all: true});

    columns: KalTableHeader<T>[] = [
        {
            code: 'NomBenefOD',
            label: 'Bénéficiaire OD',
            sortable: true,
            exporter: (el) => this.getBeneficiaryName(el.NomBenefOD, el.PrenomBenefOD, el.MatriculeBenefOD)
        },
        {
            code: 'NomBenefCible',
            label: 'Bénéficiaire cible',
            sortable: true,
            exporter: (el) => this.getBeneficiaryName(el.NomBenefCible, el.PrenomBenefCible, el.MatriculeBenefCible)
        },
        {code: 'TypeBenef', label: 'Type cible', sortable: true},
        {code: 'MontantAttribution', label: 'Montant Sub.', sortable: true, exporter: exportAmount},
        {code: 'Site', label: 'Site', sortable: true},
        {code: 'TypeContrat', label: 'Contrat', sortable: true},
        {code: 'QuantiteControl', label: 'Quantité', sortable: true}
    ];

    quantity: number;

    beneficiariesODAmount = 0;

    subscription: Subscription;

    dateLabel = null;

    sitesList: SiteBaseModel[] = [];

    contractsList: ChampListeValeurDto[] = [];

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly fb: FormBuilder,
                private readonly snackbarService: KalSnackbarService,
                private readonly layoutService: LayoutService,
                private readonly progressTracker: ProgressTrackerService) {
        super(cdr);
    }

    get allocation(): SharedAllocationExecution {
        return this.snapshot(SharedAllocationExecutionState.allocation);
    }

    get code(): SharedAllocationExecutionCode {
        return this.snapshot(SharedAllocationExecutionState.code);
    }

    get displayedColumns(): (keyof T)[] {
        return this.filteredColumns.map(({code}) => code);
    }

    get selectedBeneficiariesList(): T[] {
        return filterListFromSelection(this.displayedBeneficiariesList, this.selection, 'id');
    }

    get libelleExecution(): string {
        return this.code === 'RAN' ? 'Exécuter la remise à zéro' : 'Exécuter l\'attribution';
    }

    private get filteredColumns(): KalTableHeader<T>[] {
        return this.columns.filter(e => {
            return (this.allocation.isNominatif || !(['NomBenefCible', 'TypeBenef'] as (keyof T)[]).includes(e.code))
                && (this.code === 'ART' ? e.code !== 'MontantAttribution' : e.code !== 'QuantiteControl');
        });
    }

    countBeneficaries(type?: string): number {
        return this.selectedBeneficiariesList.filter(({TypeBenef}) => TypeBenef === type).length;
    }

    getBeneficiaryName(firstName: string, lastName: string, registrationNumber: string): string {
        return lastName + ' ' + firstName + (registrationNumber ? ' (' + registrationNumber + ')' : '');
    }

    getBeneficiaryType(allocation: T): string {
        switch (allocation.TypeBenef) {
            case 'OD':
                return 'Ouvrant droit';
            case 'CON':
                return 'Conjoint';
            case 'ENF':
                return 'Enfant';
            case 'EXT':
                return 'Externe';
        }
    }

    executeAllocation(): void {
        if (this.form.invalid) {
            return;
        }

        const {DateAttribution, Libelle, Comment} = this.form.value;

        this.loading = true;

        const params: ModeleExecAttributionBudgetDtoInput = {
            IdAttribution: this.allocation.id,
            Libelle,
            ContextAttBDG: this.code,
            DateValidationAttBDG: getISODate(DateAttribution),
            ListeAttBdgBenefAttribue: {
                AttributionBenefExecModelDTO: this.selectedBeneficiariesList.map(e => ({IdLigneVue: e.IdLigneVue}))
            },
            ListeIdLgeVueBenefNotAttribue: {
                decimal: this.beneficiariesList.filter(e => {
                    return !this.selectedBeneficiariesList.find(({IdLigneVue}) => IdLigneVue === e.IdLigneVue);
                }).map(e => e.IdLigneVue)
            },
            RemarquePrive: Comment.privateComment,
            RemarquePublic: Comment.publicComment
        };

        if (this.code === AllocationsContext.ART) {
            params.IdActivite = this.snapshot(ActivitySalesState.currentActivityId);
            params.IdArticle = this.allocation.articleId;
        }

        this.watch$ = this.dispatch$(new SharedAllocationExecutionExecuteAllocationAction(params)).pipe(
            tap(() => {
                const title = this.code === AllocationsContext.RAN ? 'La remise à zéro a bien été exécutée' : 'L\'attribution a bien été exécutée';
                this.snackbarService.open({title});
                this.watch$ = this.navigate();
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    cancel(): void {
        this.watch$ = this.navigate();
    }

    export(): void {
        const formattedBeneficiariesList = this.beneficiariesList.map((e: any) => {
            return {
                ...e,
                QuantiteControl: (this.code === AllocationsContext.ART) ? e.QuantiteControl.value : e.QuantiteControl,
                Site: e.Site?.Valeur,
                TypeContrat: e.TypeContrat?.Valeur
            };
        });

        exportListAsCSV(formattedBeneficiariesList, this.filteredColumns as ColumnsCSV<T>[], 'list_beneficiaires_execution_attribution');
    }

    stopPropagation($event: MouseEvent): void {
        $event.stopPropagation();
    }

    private navigate(): Observable<any> {
        return this.layoutService.navigateBack(null, {
            queryParams: {fromAllocationExecution: 1}
        });
    }

    private getTotalAmount(): void {
        this.beneficiariesODAmount = this.selectedBeneficiariesList.reduce((acc, {
            QuantiteControl,
            MontantAttribution
        }) => {
            return acc + (this.code === AllocationsContext.ART ? +QuantiteControl.value : MontantAttribution);
        }, 0);
        this.cdr.markForCheck();
    }

    private updateForm(): void {
        let allocationLabel = 'Attribution : ';
        switch(this.code){
            case AllocationsContext.RAN :
                allocationLabel = 'RAZ : ';
                break;
            case AllocationsContext.PMV :
                allocationLabel = 'Abondement : ';
                break;
        }

        this.form.patchValue({
            DateAttribution: getKalDateFromObject(this.executionModel, 'DateAttribution'),
            Libelle: (allocationLabel + this.executionModel.Libelle).substring(0, LIBELLE_MAX_LENGTH),
            Comment: {
                publicComment: this.executionModel.RemarquePublic,
                privateComment: this.executionModel.RemarquePrive
            }
        }, {emitEvent: false});
    }

    private getBeneficiariesList$(): Observable<any> {
        return this.graphqlService.mutateOne<'GetModeleChargementExecAttribution', MutationGetModeleChargementExecAttributionArgs>({
            mutation: gql`
                mutation GetModeleChargementExecAttribution(
                    $contexte: ContextInfosInput,
                    $idAtb: Float,
                    $ContextAttBDG: String
                ) {
                    GetModeleChargementExecAttribution(
                        contexte: $contexte
                        idAtb: $idAtb
                        ContextAttBDG: $ContextAttBDG
                    ) {
                        Libelle
                        IdAttribution
                        DateAttribution
                        IsSurEnveloppeSynchronisee
                        ListeAttributionBenefModele {
                            AttributionBenefModelDTO {
                                IdLigneVue
                                NomBenefOD
                                PrenomBenefOD
                                MatriculeBenefOD
                                NomBenefCible
                                PrenomBenefCible
                                MatriculeBenefCible
                                TypeBenef
                                MontantAttribution
                                Site {
                                    Id
                                    Valeur
                                }
                                TypeContrat {
                                    Id
                                    Valeur
                                }
                            }
                        }
                    }
                }
            `,
            variables: {
                idAtb: this.allocation.id,
                ContextAttBDG: this.code,
            }
        }).pipe(
            tap(GetModeleChargementExecAttribution => {
                this.subscription?.unsubscribe();

                this.executionModel = GetModeleChargementExecAttribution;
                this.beneficiariesList = (GetModeleChargementExecAttribution?.ListeAttributionBenefModele?.AttributionBenefModelDTO as T[])?.map(e => {
                    const allocation = {
                        id: e.IdLigneVue,
                        ...e,
                    };

                    if (this.code === 'ART') {
                        allocation.QuantiteControl = new FormControl(
                            e.MontantAttribution,
                            {updateOn: 'blur'}
                        );
                    }

                    return allocation;
                }) || [];
                this.displayedBeneficiariesList = this.beneficiariesList;

                this.selection.selectAll();
                this.updateForm();
                this.initSubscription();
            }),
        );
    }

    private saveQuantity(allocationId: number, quantity: number): void {
        this.loading = true;

        this.watch$ = this.graphqlService.mutateOne<'UpdateQteAttribuee', MutationUpdateQteAttribueeArgs>({
            mutation: gql`
                mutation UpdateQteAttribuee(
                    $contexte: ContextInfosInput,
                    $idLigneVue: Float,
                    $newQte: Float
                ) {
                    UpdateQteAttribuee(
                        contexte: $contexte
                        idLigneVue: $idLigneVue
                        newQte: $newQte
                    )
                }
            `,
            variables: {
                idLigneVue: allocationId,
                newQte: quantity
            }
        }).pipe(
            tap(() => {
                this.snackbarService.open({title: 'La quantité a bien été modifiée'});
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    private initSubscription(): void {
        this.subscription = merge(
            this.selection.changes,
            ...this.code === 'ART' ? this.beneficiariesList.map(({IdLigneVue, QuantiteControl}) => {
                return QuantiteControl.valueChanges.pipe(tap(value => this.saveQuantity(IdLigneVue, +value)));
            }) : []
        ).pipe(
            this.untilDestroyed,
            startWith(null),
            tap(() => this.getTotalAmount())
        ).subscribe();
    }

    private getDateLabel(): void {
        const DateAttribution = getKalDateFromObject(this.executionModel, 'DateAttribution');
        const selectedDate = this.snapshot(ExerciceState.exercicesInfos)?.find(e => {
            const DateDebutExercice = getKalDateFromObject(e, 'DateDebutExercice');
            const DateFinExercice = getKalDateFromObject(e, 'DateFinExercice');
            return DateAttribution.isSameOrAfter(DateDebutExercice) && DateAttribution.isSameOrBefore(DateFinExercice);
        });

        if (selectedDate.IsCloture) {
            this.dateLabel = 'La date que vous avez sélectionnée correspond à un exercice clôturé, vous ne pouvez pas entrer de nouvelles écritures avec cette date.';
        } else if (selectedDate.IsEnCoursDeCloture) {
            this.dateLabel = 'La date que vous avez sélectionnée correspond à un exercice en cours de clôture. Votre écriture pourrait influencer cette clôture.';
        }
    }

    private loadRelatedList$(): Observable<any> {
        type mutations = 'GetListeSiteBaseModel' | 'GetListeChampGenerique';
        type types = MutationGetListeSiteBaseModelArgs | MutationGetListeChampGeneriqueArgs;

        return this.graphqlService.mutateMultiple<mutations, types>({
            mutation: gql`
                mutation GetListeSiteBaseModel($contexte: ContextInfosInput) {
                    GetListeSiteBaseModel(contexte: $contexte) {
                        SiteBaseModel {
                            IdSite
                            NomSite
                        }
                    }
                    GetListeChampGenerique(
                        contexte: $contexte
                        critere: {
                            Code: "CTRAT",
                            TakeChampListeValeur: true
                            }
                        ) {
                            ChampDTO {
                                ListChampListeValeur {
                                    ChampListeValeurDTO {
                                        Id
                                        Valeur
                                        IsCloture
                                }
                            }
                        }
                    }
                }
            `,
        }).pipe(
            tap(({GetListeSiteBaseModel, GetListeChampGenerique}) => {
                this.sitesList = GetListeSiteBaseModel?.SiteBaseModel || [];
                this.contractsList = GetListeChampGenerique?.ChampDTO?.[0]?.ListChampListeValeur?.ChampListeValeurDTO || [];

                this.cdr.markForCheck();
            })
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            DateAttribution: ['', [FormValidator.date, FormValidator.isNotNull]],
            Libelle: ['', [FormValidator.isNotNull, Validators.maxLength(LIBELLE_MAX_LENGTH)]],
            Comment: [null]
        });

        this.filtersForm = this.fb.group({
            Site: [null],
            Contrat: [null]
        });

        this.watch$ = this.filtersForm.valueChanges.pipe(
            distinctUntilChanged(),
            tap(value => {
                this.displayedBeneficiariesList = [...this.beneficiariesList.filter(e => {
                    return (!value.Site || e.Site?.Id === value.Site) && (!value.Contrat || e.TypeContrat?.Id === value?.Contrat);
                })];
            })
        );

        this.watch$ = this.progressTracker.execute(ATTRIBUTION_PREPARATION, [
            () => forkJoin([
                this.getBeneficiariesList$(),
                this.dispatch$(new GetListeExerciceInfosAction()),
                this.loadRelatedList$()
            ])
        ]).pipe(
            tap(() => this.getDateLabel()),
            catchError(err => {
                if (!this.executionModel) {
                    this.snackbarService.open({title: err?.message});
                    return this.navigate();
                }

                return throwError(() => err);
            })
        );
    }

}
